import { methodAccessCatalog } from '../../api/access.js';
import { CORE_API_BASELINE } from '../../api/baseline.js';
import { Notifications } from '../../api/methods.js';
import { RpcError } from '../../client/errors.js';
import type { ParsedArgs } from '../args.js';
import { COMMAND_CATALOG } from '../catalog.js';
import type { CommandResult } from '../output.js';
import { withClient } from './common.js';

interface ProbeResult {
  method: string;
  supported: boolean | null;
  errorKind?: 'unsupported' | 'unavailable';
}

export async function capabilitiesCommand(args: ParsedArgs): Promise<CommandResult> {
  const live = await withClient(args.options, async (client) => {
    const version = client.info;
    const probes = await Promise.all([
      probe(() => client.request('health').then(() => undefined), 'health'),
      probe(() => client.request('clients.current').then(() => undefined), 'clients.current'),
    ]);
    return {
      device: client.device.id,
      version,
      encrypted: client.encrypted,
      compatibility: coreCompatibilityEvidence(version?.version),
      probes,
    };
  });

  const methods = methodAccessCatalog();
  const data = {
    live,
    cli: {
      baseline: CORE_API_BASELINE,
      commandSchemaVersion: 1,
      commands: COMMAND_CATALOG.length,
      methods: {
        total: Object.keys(methods).length,
        read: Object.values(methods).filter((effect) => effect === 'read').length,
        write: Object.values(methods).filter((effect) => effect === 'write').length,
      },
      notifications: Object.keys(Notifications).length,
      discovery: {
        mode: 'safe-probe',
        limitation:
          'Core exposes no method-introspection contract; unprobed method availability is unverified without an explicit documented compatibility signal.',
      },
    },
  };
  return { data, human: `Capabilities inspected for ${live.device}` };
}

async function probe(operation: () => Promise<void>, method: string): Promise<ProbeResult> {
  try {
    await operation();
    return { method, supported: true };
  } catch (error) {
    if (error instanceof RpcError && error.rpc.code === -32601) {
      return { method, supported: false, errorKind: 'unsupported' };
    }
    return { method, supported: null, errorKind: 'unavailable' };
  }
}

export function coreCompatibilityEvidence(version: string | undefined): {
  status: 'unverified';
  coreVersion: string | null;
  baselineVersion: string;
  reason: string;
} {
  return {
    status: 'unverified',
    coreVersion: version ?? null,
    baselineVersion: CORE_API_BASELINE.version,
    reason:
      'Core version is informational only; use successful probes or an explicit documented compatibility signal for method availability.',
  };
}
